import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowUpRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { optimizeImage, optimizeImageSrcSet } from "@/lib/images";

const ProductCard = ({ product, index = 0, hideQty = false, ctaLabel = "Ajouter", goToProduct = false }) => {
  const { addItem, setOpen } = useCart();
  const [qty, setQty] = useState(1);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, hideQty ? 1 : qty);
    toast.success(`${hideQty ? 1 : qty} × ${product.name} ajouté au panier`, {
      action: { label: "Voir le panier", onClick: () => setOpen(true) },
    });
    setQty(1);
  };

  const stopProp = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Link
      to={`/produit/${product.slug}`}
      className="group flex flex-col card-cdm fade-up"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative aspect-square overflow-hidden bg-[#F3EFE9]">
        <img
          src={optimizeImage(product.image, 600)}
          srcSet={optimizeImageSrcSet(product.image, [320, 480, 640, 800])}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={product.name}
          loading={index < 4 ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={index < 2 ? "high" : "auto"}
          className={`absolute inset-0 h-full w-full object-contain transition-transform duration-500 group-hover:scale-105 ${
            product.image_zoom === "large"
              ? "p-0"
              : product.image_zoom === "small"
              ? "p-6"
              : "p-3"
          }`}
        />
        <span
          className={`absolute top-2.5 left-2.5 inline-block backdrop-blur text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded-full ${
            product.category === "Cartons reconditionnés"
              ? "bg-[#dc2626] text-white"
              : "bg-white/95 text-[#0f1d2b]"
          }`}
        >
          {product.category === "Cartons reconditionnés" ? "Reconditionné" : product.category}
        </span>
        <div className="absolute top-2.5 right-2.5 h-8 w-8 bg-white border border-[#e6edf3] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
          <ArrowUpRight className="h-3.5 w-3.5" />
        </div>
      </div>

      <div className="flex flex-col flex-1 p-3.5">
        <h3 className="font-extrabold text-[14px] leading-tight text-[#0f1d2b] line-clamp-2 min-h-[2.5rem]">
          {product.name}
        </h3>
        <div className="mt-1 text-[11px] text-[#5b6b7a]">{product.dimensions}</div>

        {!hideQty && (
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="font-extrabold text-[18px] text-[#0f1d2b] leading-none" data-testid={`product-price-${product.slug}`}>
              {product.price.toFixed(2).replace(".", ",")} €
            </span>
            <span className="text-[11px] text-[#5b6b7a]">/ unité</span>
          </div>
        )}

        <div className="mt-auto pt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          {hideQty ? (
            <div className="sm:flex-1 flex items-baseline gap-1 whitespace-nowrap" data-testid={`product-price-${product.slug}`}>
              <span className="font-extrabold text-[18px] text-[#0f1d2b] leading-none">
                {product.price.toFixed(2).replace(".", ",")} €
              </span>
              <span className="text-[11px] text-[#5b6b7a]">/ unité</span>
            </div>
          ) : (
            <div
              className="sm:flex-1 inline-flex items-center justify-between h-9 border border-[#e6edf3] rounded-lg bg-white"
              onClick={stopProp}
            >
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQty((q) => Math.max(1, q - 1)); }}
                className="h-full flex-1 inline-flex items-center justify-center text-[#0f1d2b] hover:bg-[#f7fafc] rounded-l-lg"
                aria-label="Diminuer"
                data-testid={`qty-decrease-${product.slug}`}
              >
                <Minus className="h-3 w-3" />
              </button>
              <span
                className="px-1 text-center text-[12px] font-bold text-[#0f1d2b]"
                data-testid={`qty-value-${product.slug}`}
              >
                {qty}
              </span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQty((q) => q + 1); }}
                className="h-full flex-1 inline-flex items-center justify-center text-[#0f1d2b] hover:bg-[#f7fafc] rounded-r-lg"
                aria-label="Augmenter"
                data-testid={`qty-increase-${product.slug}`}
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={goToProduct ? undefined : handleAdd}
            className="inline-flex items-center justify-center gap-1 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[12px] font-semibold px-3 h-9 rounded-lg transition-colors whitespace-nowrap w-full sm:w-auto"
            data-testid={`add-to-cart-${product.slug}`}
          >
            <Plus className="h-3.5 w-3.5" /> {goToProduct ? "Acheter" : ctaLabel}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
